import type { HostedEvent } from "@hobbyshopify/backend-client";
import { useId, useRef } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { ShareButton } from "../../components/ShareButton";
import {
  cancelWarning,
  owedBadge,
  owedLine,
  owedState,
  showDates,
  showStatus,
  venueLine,
} from "../../core/primitives/hosted-event";
import { TableKindsSection } from "./sections/table-kinds/table-kinds-section";
import { TableKindsSectionState } from "./sections/table-kinds/table-kinds-section-model";
import { TicketKindsSection } from "./sections/ticket-kinds/ticket-kinds-section";
import { ShowActions, ShowState } from "./show-model";

function OwedRefunds({ event }: { event: HostedEvent }) {
  const state = useSubtree(ShowState);
  const actions = useSubtree(ShowActions);
  const observer = useObserver();
  const form = observer.watch(state.retryForm);
  const acting = observer.watch(state.acting);
  const owed = event.refundsOwed;

  if (owed.length === 0 && !form.saved && !form.error) return null;
  return (
    <section className="panel space-y-3 p-4">
      <div className="panel-head">Refunds owed</div>
      {owed.length > 0 && (
        <>
          <p className="text-sm opacity-60">
            This money goes back to the shops that paid it, fees included. Each one not sent yet says what stopped it:
            try the refunds again, or refund that shop in your Stripe dashboard.
          </p>
          <ul className="space-y-2">
            {owed.map((refund) => (
              <li key={refund.id} className="space-y-0.5">
                <p className="text-[13px] text-white">{owedLine(refund)}</p>
                <p className="text-xs text-muted">{owedState(refund)}</p>
              </li>
            ))}
          </ul>
          <div>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={acting}
              onClick={() => void actions.retryRefunds()}
            >
              {form.busy ? "Sending…" : "Try the refunds again"}
            </button>
          </div>
        </>
      )}
      {form.saved && <p className="note">{form.saved}</p>}
      {form.error && <p className="note note-danger">{form.error}</p>}
    </section>
  );
}

function Flyer({ event }: { event: HostedEvent }) {
  const state = useSubtree(ShowState);
  const actions = useSubtree(ShowActions);
  const observer = useObserver();
  const may = observer.watch(state.may);
  const form = observer.watch(state.flyerForm);
  const acting = observer.watch(state.acting);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const url = event.flyerUrl || null;

  if (event.cancelledAt || (!url && !may.edit)) return null;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    await actions.pickFlyer(file);
    // Cleared so picking the same file again after a failure still fires a change.
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <section className="panel space-y-3 p-4">
      <div className="panel-head">Flyer</div>
      {url ? (
        <img src={url} alt={`Flyer for ${event.title}`} className="max-h-[480px] w-auto max-w-full rounded-btn" />
      ) : (
        <p className="text-sm opacity-60">No flyer yet. Shoppers see it on the show's page.</p>
      )}
      {may.edit && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="sr-only"
            disabled={acting}
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <label
            htmlFor={inputId}
            className={`btn-secondary btn-sm cursor-pointer ${acting ? "pointer-events-none opacity-50" : ""}`}
          >
            {form.busy ? "Uploading…" : url ? "Change flyer" : "Add flyer"}
          </label>
          {url && (
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={acting}
              onClick={() => void actions.removeFlyer()}
            >
              Remove flyer
            </button>
          )}
        </div>
      )}
      {form.error && <p className="note note-danger">{form.error}</p>}
    </section>
  );
}

function CancelShow() {
  const state = useSubtree(ShowState);
  const actions = useSubtree(ShowActions);
  const observer = useObserver();
  const cancelledAt = observer.watch(state.cancelledAt);
  const confirm = observer.watch(state.confirmCancel);
  const form = observer.watch(state.cancelForm);
  const paid = observer.watch(state.paidTables);
  const offered = observer.watch(state.cancelOffered);
  const acting = observer.watch(state.acting);

  if (cancelledAt) {
    return (
      <section className="panel space-y-3 p-4">
        <div className="panel-head">Cancelled</div>
        <p className="text-sm opacity-60">
          You cancelled this show on {cancelledAt.toLocaleDateString()}. It is off the calendar, and nothing about it
          changes again.
        </p>
        {form.saved && <p className="note">{form.saved}</p>}
        {form.error && <p className="note note-danger">{form.error}</p>}
      </section>
    );
  }
  if (!offered) return null;
  return (
    <section className="panel space-y-3 p-4">
      <div className="panel-head">Cancel this show</div>
      {confirm ? (
        <>
          <p className="note note-warn">{cancelWarning(paid)}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-destructive btn-sm"
              disabled={acting}
              onClick={() => void actions.cancelShow()}
            >
              {form.busy ? "Cancelling…" : "Yes, cancel it"}
            </button>
            <button type="button" className="btn-ghost btn-sm" disabled={form.busy} onClick={actions.keepShow}>
              Keep it
            </button>
          </div>
        </>
      ) : (
        <div>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={acting}
            onClick={() => void actions.cancelShow()}
          >
            Cancel show
          </button>
        </div>
      )}
      {form.error && <p className="note note-danger">{form.error}</p>}
    </section>
  );
}

function ShowHeader({ event }: { event: HostedEvent }) {
  const state = useSubtree(ShowState);
  const actions = useSubtree(ShowActions);
  const observer = useObserver();
  const may = observer.watch(state.may);
  const form = observer.watch(state.publishForm);
  const acting = observer.watch(state.acting);
  const share = observer.watch(state.share);
  const status = showStatus(event);
  const owed = event.refundsOwed.length;
  const link = event.shareUrl && /^https?:\/\//i.test(event.shareUrl) ? event.shareUrl : null;

  return (
    <header className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">{event.title}</h1>
            <span className={`badge shrink-0 ${status.cls}`}>{status.label}</span>
            {owed > 0 && <span className="badge badge-danger shrink-0">{owedBadge(owed)}</span>}
          </div>
          <p className="text-sm opacity-60">{showDates(event)}</p>
          <p className="text-sm opacity-60">{venueLine(event.place)}</p>
          {link && (
            <p className="truncate text-sm">
              <a href={link} target="_blank" rel="noreferrer" className="underline opacity-60">
                {link}
              </a>
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {share && (
            <ShareButton
              target={share}
              heading="Share this show"
              className="btn-ghost"
              note="Vendors can buy a table and visitors add it to their calendar from this page."
            />
          )}
          {may.edit && (
            <button type="button" className="btn-ghost" onClick={actions.edit}>
              Edit
            </button>
          )}
          {may.takeDown && (
            <button type="button" className="btn-secondary" disabled={acting} onClick={() => void actions.unpublish()}>
              {form.busy ? "Taking down…" : "Take down"}
            </button>
          )}
          {may.publish && (
            <button type="button" className="btn-primary" disabled={acting} onClick={() => void actions.publish()}>
              {form.busy ? "Publishing…" : "Publish"}
            </button>
          )}
        </div>
      </div>
      {event.description && <p className="whitespace-pre-line text-sm opacity-80">{event.description}</p>}
      {form.saved && <p className="note">{form.saved}</p>}
      {form.error && <p className="note note-danger">{form.error}</p>}
    </header>
  );
}

export function ShowPage() {
  const state = useSubtree(ShowState);
  const actions = useSubtree(ShowActions);
  const tables = useSubtree(TableKindsSectionState);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const event = observer.watch(state.event);
  const may = observer.watch(state.may);
  const kinds = observer.watch(tables.kinds);
  const tablesStatus = observer.watch(tables.status);

  const noTables = tablesStatus.kind === "loaded" && kinds.length === 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <button type="button" className="text-xs text-muted hover:text-white" onClick={actions.back}>
        ← Shows
      </button>

      {status.kind === "loading" && !event && <p className="opacity-60">Loading the show…</p>}
      {status.kind === "error" && <p className="note note-danger">{status.message}</p>}

      {event && status.kind !== "error" && (
        <>
          <ShowHeader event={event} />
          <Flyer event={event} />
          {may.datesPassed && <p className="note">This show's dates have passed. Change them to publish it.</p>}
          {may.publish && noTables && (
            <p className="note">Next, add the tables other shops can take. Then publish the show.</p>
          )}
          <TableKindsSection />
          <TicketKindsSection />
          <OwedRefunds event={event} />
          <CancelShow />
        </>
      )}
    </div>
  );
}
